import * as React from "react";
import { each, map, reduce, size } from "lodash";
import { Waypoint } from "react-waypoint";
import styled from "styled-components";
import { content, transparentize } from "@glossy";
import Checkbox from "@komon/form/checkbox";
import { Locale } from "@linkfluence/models/locale";
import { canManageProject } from "@linkfluence/models/role";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import SafeHTML from "@ui-kit/safe-html";
import Tooltip from "@ui-kit/tooltip";
import Link from "@ui-kit/utils/link";
import Empty from "@radarly/components/navigation/switcher/empty";
import Loading from "@radarly/components/navigation/switcher/loading";
import ProjectPortrait from "@radarly/components/portrait/project";
import QueryPortrait from "@radarly/components/portrait/query";
import TooltipProject from "@radarly/components/tooltip/project";
import { tr } from "@radarly/i18n";
import {
  SearchableQuery,
  SplittedSearchableQuery,
} from "@radarly/models/queries";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";

const QueryContainer = styled.div`
  position: relative;
`;

const Query = styled(Link)`
  color: ${content};
  padding: 16px;
  display: block;
  font-size: 14px;
  text-decoration: none;
  width: 100%;
  &:not(:last-child) {
    border-bottom: solid 1px ${(props) => content(props, "tertiary")};
  }
  &:hover {
    background-color: ${(props) => transparentize(0.85, content(props))};
    text-decoration: none;
  }
`;

const Header = styled.div`
  margin-bottom: 3px;
`;

const Label = styled.div`
  display: inline-block;
  margin-left: 8px;
  font-size: 14px;
  em {
    font-weight: bold;
  }
`;

const Actions = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  a {
    color: ${content};
  }
`;

const Action = styled.span`
  margin-left: 5px;
  cursor: pointer;
  display: inline-block;
`;

const ActionLink = styled.a`
  margin-left: 5px;
  cursor: pointer;
  display: inline-block;
`;

const Highlight = styled.div`
  font-size: 12px;
  color: ${(props) => transparentize(0.5, content(props))};
  em {
    color: ${content};
    font-weight: bold;
  }
`;

const WorkspaceCard = styled.div`
  margin-top: 5px;
  display: inline-block;
  font-size: 12px;
  line-height: 12px;
`;

const Arrow = styled.span`
  &:after {
    content: "›";
    width: 14px;
    display: inline-block;
    text-align: center;
    font-size: 16px;
    line-height: 1.1em;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  display: inline-block;
  margin-right: 16px;
`;

const Checkboxes = styled.div`
  padding: 7px 16px;
  height: 42px;
  border-bottom: solid 1px ${(props) => content(props, "tertiary")};
  box-sizing: border-box;
`;

function splitQueriesByWorkspaces(queries: SearchableQuery[]) {
  return reduce(
    queries,
    (acc: SplittedSearchableQuery[], { dashboards: workspaces, ...query }) => {
      if (size(workspaces)) {
        each(workspaces, (workspace) => {
          acc.push({
            ...query,
            workspace,
          });
        });
      } else {
        acc.push({ ...query, workspace: null });
      }
      return acc;
    },
    []
  );
}

function getPictureUrl(query: SplittedSearchableQuery) {
  if (query.workspace) {
    return query.workspace.imageUrl || query.project.imageUrl;
  } else {
    return query.project.imageUrl;
  }
}

interface IProps {
  queries: SearchableQuery[];
  locale: Locale;
  loading?: boolean;
  searchIntoName?: boolean;
  searchIntoQuery?: boolean;
  onChangeSearchIntoName?: () => void;
  onChangeSearchIntoQuery?: () => void;
  openQueryInfo?: (query: number) => void;
  openSettings?: () => void;
  refineSearch?: () => void;
  onClickQuery?: (query: number) => void;
  canFetchMore: boolean;
  fetchMore: () => void;
  router: IRouterService;
  timezone: Timezone;
}

const RadarlyComponentsNavigationSwitcherSearch = ({
  queries,
  locale,
  openQueryInfo,
  openSettings,
  onChangeSearchIntoName,
  searchIntoName,
  onChangeSearchIntoQuery,
  searchIntoQuery,
  refineSearch,
  loading,
  onClickQuery,
  router,
  fetchMore,
  canFetchMore,
  timezone,
}: IProps) => {
  const sizeQueries = size(queries);
  const _openQueryInfo = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, focus: number) => {
      event.preventDefault();
      event.stopPropagation();
      openQueryInfo(focus);
    },
    [openQueryInfo]
  );
  return (
    <>
      <Checkboxes>
        <StyledCheckbox
          label={tr(locale, "QUERIES_SEARCH::SEARCH_IN_LABELS")}
          onChange={onChangeSearchIntoName}
          locale={locale}
          checked={searchIntoName}
        />
        <StyledCheckbox
          label={tr(locale, "QUERIES_SEARCH::SEARCH_IN_TERMS")}
          onChange={onChangeSearchIntoQuery}
          locale={locale}
          checked={searchIntoQuery}
        />
      </Checkboxes>
      {loading && !sizeQueries ? (
        <Loading />
      ) : sizeQueries ? (
        map(splitQueriesByWorkspaces(queries), (query) => {
          return (
            <QueryContainer
              key={query.id + "_" + (query.workspace ? query.workspace.id : 0)}
            >
              <Query
                url={
                  query.workspace && query.project.isAccessible
                    ? router.getWorkspaceUrlFromPage(
                        Page.listeningAnalytics,
                        query.project,
                        query.workspace.id,
                        { filters: { focuses: [query.id] }, timezone }
                      )
                    : null
                }
                onClick={() => onClickQuery(query.id)}
              >
                <Header>
                  <QueryPortrait
                    picture={query.imageUrl}
                    borderColor={query.color}
                    size="s"
                  />
                  <SafeHTML
                    as={Label}
                    html={query.highlight["metadata.label"] || query.label}
                  />
                </Header>
                {!!searchIntoQuery && (
                  <SafeHTML
                    as={Highlight}
                    html={query.highlight["metadata.query"] || query.query}
                  />
                )}
                <TooltipProject
                  locale={locale}
                  isAccessible={query.project.isAccessible}
                  isActive={query.project.isActive}
                  placement="left"
                >
                  <WorkspaceCard>
                    <span>{query.project.label}</span>
                    <Arrow />
                    <ProjectPortrait
                      picture={getPictureUrl(query)}
                      isAccessible={query.project.isAccessible}
                      isActive={query.project.isActive}
                      size={16}
                    />{" "}
                    {query.workspace ? (
                      <span>{query.workspace.label}</span>
                    ) : (
                      <span>
                        {tr(locale, "QUERIES_SEARCH.RESULT.NO_DASHBOARD")}
                      </span>
                    )}
                  </WorkspaceCard>
                </TooltipProject>
              </Query>

              <Actions>
                {canManageProject(query.project.role) &&
                  query.project.isAccessible && (
                    <Tooltip
                      tooltip={tr(
                        locale,
                        "QUERIES_SEARCH::RESULT::SETTINGS_TOOLTIP"
                      )}
                      placement="left"
                    >
                      <ActionLink
                        href={router.getSettingsUrl(
                          query.project,
                          "/queries/" + query.id
                        )}
                      >
                        <Icon glyph="cog" />
                      </ActionLink>
                    </Tooltip>
                  )}
                <Tooltip
                  tooltip={tr(locale, "QUERIES_SEARCH::RESULT::INFO_TOOLTIP")}
                  placement="left"
                >
                  <Action onClick={(event) => _openQueryInfo(event, query.id)}>
                    <Icon glyph="circle-info" />
                  </Action>
                </Tooltip>
              </Actions>
            </QueryContainer>
          );
        })
      ) : (
        <Empty label={tr(locale, "QUERIES_SEARCH::NO_RESULTS")} />
      )}
      {canFetchMore && !loading && <Waypoint onEnter={() => fetchMore()} />}
    </>
  );
};

export default RadarlyComponentsNavigationSwitcherSearch;
